/**
 * The flight search session lives in sessionStorage (per browser tab) so the
 * countdown survives reloads, back navigation and the round trip to the bank's
 * 3D Secure page, instead of restarting whenever the page remounts.
 */
const STORAGE_KEY = 'flight-search-session';

export const FLIGHT_SESSION_WARNING_SECONDS = 5 * 60; // 5 minutes in seconds

const readStorage = () => {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

const writeStorage = value => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    // Storage can be unavailable (private mode); the countdown then relies on
    // the server sync alone.
  }
};

/**
 * What was searched for. `sc` is bumped by an explicit "search again", so a
 * repeated search on purpose starts a new session while back navigation and
 * filters keep the running one.
 */
export const getFlightSearchKey = search =>
  JSON.stringify([
    search?.departureDate || null,
    search?.returnDate || null,
    search?.origin?.value || null,
    !!search?.origin?.isCity,
    search?.destination?.value || null,
    !!search?.destination?.isCity,
    search?.cabinClassTravellers?.cabinClass || null,
    search?.cabinClassTravellers?.travellers?.adultsCount || 0,
    search?.cabinClassTravellers?.travellers?.childrenCount || 0,
    search?.cabinClassTravellers?.travellers?.infantsCount || 0,
    Number.isFinite(search?.sc) ? search.sc : 0,
  ]);

export const readFlightSearchSession = () => readStorage();

/**
 * The last session this tab used, whatever it was for. It is always sent with
 * a search so the API can keep it or, for new search params, delete its data
 * and replace it, leaving the user with a single session.
 */
export const getLastFlightSearchSession = () => readStorage()?.session || null;

/**
 * True when `search` must not continue the last session: the params changed,
 * "search again" bumped `sc`, or the user chose to leave that session.
 */
export const shouldRenewFlightSearchSession = search => {
  const stored = readStorage();
  return (
    !!stored?.session &&
    (!!stored.renewNext || stored.searchKey !== getFlightSearchKey(search))
  );
};

/** The next search replaces the last session instead of continuing it. */
export const markFlightSearchSessionForRenewal = () => {
  const stored = readStorage();
  if (stored?.session) writeStorage({ ...stored, renewNext: true });
};

export const saveFlightSearchSession = ({
  session,
  search,
  remainingSeconds,
}) => {
  if (!session) return null;
  const stored = readStorage();
  const searchKey = getFlightSearchKey(search);
  // The API keeps the session id across searches, so a renewed session is the
  // same id with a new search; only a continued one keeps its old state.
  const continuesSession =
    stored?.session === session &&
    stored.searchKey === searchKey &&
    !stored.renewNext;
  const expiresAt =
    remainingSeconds > 0
      ? Date.now() + remainingSeconds * 1000
      : (continuesSession && stored.expiresAt) || null;
  writeStorage({
    session,
    search,
    searchKey,
    expiresAt,
    warningDismissed: continuesSession ? !!stored.warningDismissed : false,
  });
  return expiresAt;
};

/** Re-anchors the stored expiry to the server's remaining seconds. */
export const syncFlightSearchSessionExpiry = (session, remainingSeconds) => {
  const stored = readStorage();
  const expiresAt = Date.now() + Math.max(0, remainingSeconds) * 1000;
  writeStorage({
    ...(stored?.session === session ? stored : { session }),
    expiresAt,
  });
  return expiresAt;
};

export const getStoredFlightSessionExpiry = session => {
  const stored = readStorage();
  return stored?.session === session ? stored.expiresAt ?? null : null;
};

export const isFlightSessionWarningDismissed = session => {
  const stored = readStorage();
  return stored?.session === session && !!stored.warningDismissed;
};

export const dismissFlightSessionWarning = session => {
  const stored = readStorage();
  if (stored?.session === session) {
    writeStorage({ ...stored, warningDismissed: true });
  }
};
